import HeaderFilterButton from 'app/ProviderSearch/HeaderFilterButton'
import { filterTitle } from 'constants/common'
import { ProviderHeaderFilterTypes } from 'dataSource/ProviderSearch'
import styles from './index.module.scss'
import React, { useEffect, useState } from 'react'
import useAppSelector from 'hooks/useAppSelector'
import classNames from 'classnames'
import _omit from 'lodash/omit'
import B2bVerifyFormModal from 'common/B2BVerification/B2bVerifyFormModal'
import B2bVerifyMessageModal from 'common/B2BVerification/B2bVerifyMessageModal'
import { fetchAllBusinessPartnersRoute } from 'dataSource/ProviderSearch/routes'
import useApi from 'hooks/useApi'
import { useRouter } from 'next/router'
import useSalesforceCase from 'hooks/salesforce/useSalesforceCase'

type B2BClientFilterProps = {
  b2bButtonClass?: string
  containerClass?: string
}

const B2BClientFilter: React.FC<B2BClientFilterProps> = ({
  b2bButtonClass = '',
  containerClass = '',
}) => {
  const [showB2bModal, setShowB2bModal] = useState<boolean>(false)
  const { request: businessPartnerRequest } = useApi()
  const { b2bUserDetails } =
    useAppSelector((state) => state.bookingHelper) ?? {}
  const { verificationMessages, isB2BUser = false } = b2bUserDetails ?? {}
  const router = useRouter()
  const { partnershipCode } = router.query
  const [businessPartners, setBusinessPartners] = useState<
    B2bPartnerOptionDetails[]
  >([])
  const [showFormModal, setShowFormModal] = useState<boolean>(false)
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false)
  const { hasCaseId } = useSalesforceCase()
  const isSalesforceCase = hasCaseId

  /**
   * Load business partners data
   */
  const loadBusinessPartners = async () => {
    const limit = 500
    let offset = 0
    let totalCount = 0
    let hasMoreBusinessPartners = true
    let businessPartnersListWithDuplicates = []

    try {
      do {
        const b2bPartnersListData = await businessPartnerRequest.get(
          fetchAllBusinessPartnersRoute(offset, limit)
        )

        if (b2bPartnersListData?.result?.length > 0) {
          totalCount = b2bPartnersListData?.pagination?.count
          businessPartnersListWithDuplicates = [
            ...businessPartnersListWithDuplicates,
            ...b2bPartnersListData?.result?.map(
              (businessPartner: BusinessPartner) => ({
                value: {
                  ...businessPartner,
                },
                label: businessPartner.name,
              })
            ),
          ]
          offset += limit
        } else {
          hasMoreBusinessPartners = false
        }
      } while (
        hasMoreBusinessPartners &&
        businessPartnersListWithDuplicates.length < totalCount
      )
    } catch {
      hasMoreBusinessPartners = false
    }

    // set business partners, which is all of them with duplicate names, different info
    setBusinessPartners([...businessPartnersListWithDuplicates])
  }

  useEffect(() => {
    loadBusinessPartners()
    let timerId

    if (partnershipCode && !isSalesforceCase) {
      timerId = setTimeout(() => {
        setShowMessageModal(true)
      }, 500)
    }

    return () => {
      clearTimeout(timerId)
    }
  }, [])

  const onChangeB2bFormModalState = (updatedValue: boolean) => {
    setShowFormModal(updatedValue)
  }

  const onChangeB2bMessageModalState = (updatedValue: boolean) => {
    setShowMessageModal(updatedValue)
  }

  return (
    <>
      <HeaderFilterButton
        containerClassName={classNames(
          styles.b2b_filter_button_container,
          containerClass
        )}
        title={filterTitle.B2B_CLIENT}
        showDropdown={showB2bModal}
        setShowDropdown={(showDropdown) => {
          setShowB2bModal(showDropdown)
          verificationMessages
            ? setShowMessageModal(true)
            : setShowFormModal(true)
        }}
        filterType={ProviderHeaderFilterTypes.B2B_CLIENT}
        buttonClassName={classNames(styles.b2b_filter_button, b2bButtonClass)}
        buttonTextClassName={classNames(styles.selectable_btn_text, {
          'text-info': isB2BUser,
        })}
        placeHolder="Search here..."
        text={isB2BUser ? 'View Details' : ''}
        mobilePlaceHolder={filterTitle.B2B_CLIENT}
        disableOutsideClick
        disableMobileFilterModal
      />
      {showFormModal && (
        <B2bVerifyFormModal
          businessPartners={businessPartners}
          handleChangeB2bFormModal={onChangeB2bFormModalState}
          handleChangeB2bMessageModal={onChangeB2bMessageModalState}
        />
      )}
      {showMessageModal && (
        <B2bVerifyMessageModal
          handleChangeB2bFormModal={onChangeB2bFormModalState}
          handleChangeB2bMessageModal={onChangeB2bMessageModalState}
        />
      )}
    </>
  )
}

export default B2BClientFilter
